<template>
  <div
    class="cn-textarea"
    :class="{
      'cn-textarea--with-icon': icon,
      'cn-textarea--error': error,
    }"
  >
    <div class="cn-textarea__wrapper">
      <UiIcon v-if="icon" class="cn-textarea__icon" :name="icon" />
      <textarea
        v-model="valueLocal"
        type="text"
        class="cn-textarea__element"
        :placeholder="placeholder"
        rows="4"
      />
    </div>

    <p v-if="error" class="cn-textarea__error">
      {{ error }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { Icon } from '@/components/ui/UiIcon.vue';
import UiIcon from '@/components/ui/UiIcon';

interface Props {
  icon?: Icon;
  placeholder?: string;
  error?: string;
  modelValue: string | number | string[];
}
const props = withDefaults(defineProps<Props>(), {
  icon: undefined,
  placeholder: undefined,
  error: undefined,
});

const emit = defineEmits<{
  (e: 'update:modelValue', data: string | number | string[]): void;
}>();

const valueLocal = computed({
  get() {
    return props.modelValue;
  },
  set(newValue) {
    emit('update:modelValue', newValue);
  },
});
</script>

<style lang="scss">
.cn-textarea {
  --ab-canyon-textarea-icon-color-text: var(--ab-canyon-color-neutral-dark);
  --ab-canyon-textarea-element-color-bg: var(--ab-canyon-color-light);
  --ab-canyon-textarea-element-color-text: var(--ab-canyon-color-dark);
  --ab-canyon-textarea-element-placeholder-color-text: var(
    --ab-canyon-color-neutral-dark
  );
  --ab-canyon-textarea-element-color-border: var(--ab-canyon-color-neutral);
  --ab-canyon-textarea-element-color-border-focus: var(
    --ab-canyon-color-neutral-dark
  );
  --ab-canyon-textarea-element-color-outline-focus: var(
    --ab-canyon-color-neutral-light
  );
  --ab-canyon-textarea-element-color-border-error: var(
    --ab-canyon-color-accent
  );
  --ab-canyon-textarea-error-color-text: var(--ab-canyon-color-accent);

  width: 100%;
  position: relative;
  &__wrapper {
    position: relative;
  }
  &__icon {
    color: var(--ab-canyon-textarea-icon-color-text);
    width: var(--size-lg);
    height: var(--size-lg);
    top: var(--size-base);
    left: var(--size-xs);
    position: absolute;
  }
  &__element {
    transition-property:
      color, background-color, border-color, text-decoration-color, fill,
      stroke, opacity, box-shadow, transform, filter, backdrop-filter;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 150ms;
    color: var(--ab-canyon-textarea-element-color-text);
    background-color: var(--ab-canyon-textarea-element-color-bg);
    padding-top: var(--size-xs);
    padding-bottom: var(--size-xs);
    padding-left: var(--size-lg);
    padding-right: var(--size-lg);
    border-color: var(--ab-canyon-textarea-element-color-border);
    width: 100%;
    @media (min-width: 640px) {
      font-size: 1.125rem;
      line-height: 1.75rem;
    }
    &:focus {
      outline: none;
      outline-offset: 0;
      border-color: var(--ab-canyon-textarea-element-color-border-focus);
      box-shadow: 0px 0px 4px -1px
        color-mix(
          in srgb,
          var(--ab-canyon-textarea-element-color-border-focus),
          transparent 20%
        );
    }
    &::placeholder {
      color: var(--ab-canyon-textarea-element-placeholder-color-text);
    }
  }
  &__error {
    color: var(--ab-canyon-textarea-error-color-text);
    padding-top: calc(var(--size-base) / 4);
    padding-bottom: calc(var(--size-base) / 4);
  }
  &--with-icon {
    .cn-textarea {
      &__element {
        padding-left: var(--size-4xl);
      }
    }
  }
  &--error {
    .cn-textarea {
      &__element {
        border-color: var(--ab-canyon-textarea-element-color-border-error);
      }
    }
  }
}
</style>
